import { Button, Checkbox, Input, Radio, SelectMasterInfo } from '@/components';
import PageSizeFilter from '@/components/common/table/components/pagesize-filter';
import AutoComplete from '@/components/form/auto-complete';
import { AssignmentStatusEnum, PageSize } from '@/constant';
import { useFormikCustom } from '@/hooks';
import {
	cn,
	compareCommonProperties,
	getFieldAutoCompleteProps,
	getFieldCheckboxProps,
	getFieldInputProps,
	insertObjectIf,
	randomId,
} from '@/utils';
import {
	createContext,
	ForwardedRef,
	forwardRef,
	useCallback,
	useContext,
	useImperativeHandle,
	useMemo,
	useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { RadioChangeEvent } from 'antd';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import { DefaultOptionType } from 'antd/es/select';

import { FormikValues } from 'formik';
import { omit } from 'ramda';

import { ICheckbox } from '../checkbox';
import { type ISelectMasterInfo } from './../select-master-info';
import {
	type TFilterWithViewAll,
	type TFilterWithViewAllForward,
	type TFilterWithViewAllRef,
} from './type';

const FilterContext = createContext<any>(null);

export const FilterSelectMasterInfo = ({
	name,
	onChange,
	...props
}: ISelectMasterInfo & { name: string }) => {
	const { onChangeSelect, formik } = useContext(FilterContext);
	return (
		<SelectMasterInfo
			{...props}
			onChange={(value, option) => {
				onChangeSelect(name, value, option);
				onChange?.(value, option); // gọi callback nếu có
			}}
			value={formik?.values?.[name] || ''}
		/>
	);
};

export const FilterCheckbox = ({
	name,
	...props
}: ICheckbox & { name: string }) => {
	const { onChangeCheckboxFilter, formik } = useContext(FilterContext);
	const checked = useMemo(() => {
		if (name === 'assignmentPossibility') {
			return (
				formik?.values?.[name] === AssignmentStatusEnum.ASSIGNMENT_POSSIBLE
			);
		}
		return formik?.values?.[name] as boolean;
	}, [name, formik?.values]);

	return (
		<Checkbox
			{...props}
			{...getFieldCheckboxProps(formik, name)}
			checked={checked}
			onChange={(e: CheckboxChangeEvent) => onChangeCheckboxFilter(e, name)}
		/>
	);
};

const FilterWithViewAll = <Values extends FormikValues = FormikValues>(
	props: TFilterWithViewAll<Values>,
	ref: ForwardedRef<TFilterWithViewAllRef>
) => {
	const { t } = useTranslation('common');
	const {
		children,
		initialValues,
		inputName = 'name',
		inputPlaceholder = '',
		inputClassName = 'w-[180px]',
		buttonClassName = '',
		hideCheckbox = false,
		hidePageSize = false,
		hideInput = false,
		rowSelection,
		fetchData,

		containerClassName = '',

		showRadio = false,
		radioName = 'type',
		radioOptions = [],
		labelSubmit = t('search1'),
		isClickSearch = false,
		isShowAutoComplete = false,
		ignoreCheckAllKeys = [],
		autoCompleteProps,
	} = props;

	const _randomId = useMemo(() => randomId(), []);
	const formik = useFormikCustom({
		initialValues,
		onSubmit(values) {
			fetchData?.({
				...values,
				...insertObjectIf(showRadio, { [radioName]: radioValue }),
				page: 1,
			});
		},
	});

	const {
		values,
		setFieldValue,
		setValues,
		handleSubmit,
		initialValues: initValues,
	} = formik;

	const [radioValue, setRadioValue] = useState(values?.[radioName]);

	const onChangeSelect = useCallback(
		(name: string, value: DefaultOptionType['value']) => {
			setFieldValue(name, value);
			if (!isClickSearch) {
				handleSubmit();
			}
		},
		[isClickSearch]
	);

	const checkedAll = useMemo(() => {
		return compareCommonProperties(
			omit(['page', 'pageSize', ...ignoreCheckAllKeys], initValues),
			omit(['page', 'pageSize', ...ignoreCheckAllKeys], values)
		);
	}, [values, initValues]);

	useImperativeHandle(ref, () => {
		return {
			onReset,
			isSearching: !checkedAll,
			setFieldValue,
		};
	});

	const onReset = useCallback(
		(shouldSubmitForm?: boolean, resetRowSelection: boolean = true) => {
			if (resetRowSelection) {
				rowSelection?.onReset();
			}
			setValues((prevValues) => ({
				...prevValues,
				...omit(['pageSize', ...ignoreCheckAllKeys], initValues),
				page: 1,
			}));
			if (shouldSubmitForm) {
				handleSubmit();
			}
		},
		[rowSelection, handleSubmit, initValues]
	);

	const onPageSizeChange = useCallback((value: DefaultOptionType['value']) => {
		setValues((prev) => ({
			...prev,
			page: 1,
			pageSize: value as PageSize,
		}));
		handleSubmit();
	}, []);

	const onChangeRadio = useCallback(
		(e: RadioChangeEvent) => {
			setRadioValue(e.target.value);
			onReset(true);
		},
		[radioName, onReset]
	);

	const onChangeViewAll = useCallback(
		(e: CheckboxChangeEvent) => {
			if (checkedAll) return;

			onReset(!isClickSearch);
		},
		[checkedAll, onReset, isClickSearch]
	);

	const onKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
		if (e.key === 'Enter') {
			formik.onSubmit();
		}
	}, []);

	const onChangeCheckboxFilter = useCallback(
		(e: CheckboxChangeEvent, name: string) => {
			let value: string | boolean = e.target.checked;

			switch (name) {
				case 'assignmentPossibility':
					value = e.target.checked
						? AssignmentStatusEnum.ASSIGNMENT_POSSIBLE
						: AssignmentStatusEnum.ASSIGNMENT_ALL;
					break;
				default:
					break;
			}

			setFieldValue(name, value);

			formik.onSubmit();
		},
		[]
	);

	const typeAutoComplete = useMemo(() => {
		return autoCompleteProps?.options?.find(
			(item) => item.value === values.searchType
		)?.type;
	}, [autoCompleteProps?.options, values.searchType]);

	return (
		<FilterContext.Provider
			value={{ onChangeSelect, onChangeCheckboxFilter, formik }}
		>
			{showRadio && (
				<>
					<p className="text-[14px] font-bold mb-[5px]">제공자</p>
					<Radio
						options={radioOptions}
						containerClassName="mb-[34px]"
						name={radioName}
						value={radioValue}
						onChange={onChangeRadio}
					/>
					<p className="text-[14px] font-bold mb-[5px]">제공자 선택</p>
				</>
			)}
			<div className={cn('flex gap-x-2', containerClassName)}>
				{!hideCheckbox && (
					<Checkbox
						checked={checkedAll}
						onChange={onChangeViewAll}
						suffixLabel={t('view-all')}
						suffixLabelClassName="min-0 w-max"
						id={_randomId + '-reset'}
					/>
				)}
				{children}
				{!hideInput && (
					<>
						{isShowAutoComplete ? (
							<AutoComplete
								inputClassName={cn('!w-[376px]', inputClassName)}
								{...getFieldAutoCompleteProps(formik, inputName)}
								placeholder={inputPlaceholder}
								searchValue={formik.values[inputName]?.searchValue}
								onKeyDown={onKeyDown}
								type={typeAutoComplete}
								{...autoCompleteProps}
							/>
						) : (
							<Input
								containerClassName="flex-[0]"
								{...getFieldInputProps(formik, inputName)}
								placeholder={inputPlaceholder}
								onPressEnter={formik.onSubmit}
								inputClassName={inputClassName}
							/>
						)}

						<Button
							secondary
							onClick={formik.onSubmit}
							className={buttonClassName}
						>
							{labelSubmit}
						</Button>
					</>
				)}
				{!hidePageSize && (
					<PageSizeFilter
						onPageSizeChange={onPageSizeChange}
						defaultPageSize={values.pageSize}
					/>
				)}
			</div>
		</FilterContext.Provider>
	);
};

export default forwardRef(FilterWithViewAll) as TFilterWithViewAllForward;
